import { getPreferredBookmaker } from "../components/lib/oddsGames";
import {
  buildFallbackGameFromBet,
  findGameForStraightBet,
} from "./activeBetGameLookup";
import { findDrawOutcome, resolveH2hSides } from "./h2hOutcomes";

export function gameHasWagerableOdds(game) {
  const bookmaker = getPreferredBookmaker(game?.bookmakers || []);
  return Boolean(bookmaker?.markets?.length);
}

/** Prefer live scores from bulk list; bookmakers from resolved source. */
export function mergeBoardGameData(bulkGame, resolvedGame, legBet) {
  const base = bulkGame || buildFallbackGameFromBet(legBet);
  if (!resolvedGame) return base;

  const merged = {
    ...base,
    ...resolvedGame,
    id: resolvedGame.id || base.id,
    home_team: resolvedGame.home_team || base.home_team,
    away_team: resolvedGame.away_team || base.away_team,
    commence_time: resolvedGame.commence_time || base.commence_time,
    sport_key: resolvedGame.sport_key || base.sport_key,
    sport_title: resolvedGame.sport_title || base.sport_title,
    scores: bulkGame?.scores?.length ? bulkGame.scores : resolvedGame.scores,
    last_update: bulkGame?.last_update || resolvedGame.last_update,
  };

  if (gameHasWagerableOdds(resolvedGame)) {
    merged.bookmakers = resolvedGame.bookmakers;
  } else if (gameHasWagerableOdds(bulkGame)) {
    merged.bookmakers = bulkGame.bookmakers;
  } else {
    merged.bookmakers = resolvedGame.bookmakers || bulkGame?.bookmakers || [];
  }

  return merged;
}

/** Outcome indices for spread / h2h / total cells on the open-bet live board. */
export function getWagerableOutcomeIndices(game, homeTeam, awayTeam) {
  const bookmaker = getPreferredBookmaker(game?.bookmakers || []);
  if (!bookmaker) return null;

  const spreads = bookmaker.markets?.find((m) => m.key === "spreads");
  const h2h = bookmaker.markets?.find((m) => m.key === "h2h");
  const totals = bookmaker.markets?.find((m) => m.key === "totals");
  const h2hSides = resolveH2hSides(h2h, homeTeam, awayTeam);

  const spreadHomeIdx =
    spreads?.outcomes?.findIndex((o) => o.name === homeTeam) ?? -1;
  const spreadAwayIdx =
    spreads?.outcomes?.findIndex((o) => o.name === awayTeam) ?? -1;
  const h2hHomeIdx = h2hSides?.home?.index ?? -1;
  const h2hAwayIdx = h2hSides?.away?.index ?? -1;
  const drawIdx = findDrawOutcome(h2h).index;
  const overIdx =
    totals?.outcomes?.findIndex((o) => o.name === "Over") ?? -1;
  const underIdx =
    totals?.outcomes?.findIndex((o) => o.name === "Under") ?? -1;

  return {
    isThreeWay: h2hSides?.isThreeWay ?? false,
    home: { spreads: spreadHomeIdx, h2h: h2hHomeIdx, totals: overIdx },
    away: { spreads: spreadAwayIdx, h2h: h2hAwayIdx, totals: underIdx },
    draw: { h2h: drawIdx },
  };
}

export async function resolveOpenBetBoardGames(legs, bulkGames, fetchLegGame) {
  if (!Array.isArray(legs) || legs.length === 0) return [];

  return Promise.all(
    legs.map(async (legBet) => {
      const bulkGame = findGameForStraightBet(bulkGames, legBet);
      const bulkWithOdds = bulkGame && gameHasWagerableOdds(bulkGame) ? bulkGame : null;

      if (bulkWithOdds) {
        return mergeBoardGameData(bulkGame, bulkWithOdds, legBet);
      }

      try {
        const { status, result } = await fetchLegGame({ leg: legBet });
        if (status === true && result && gameHasWagerableOdds(result)) {
          return mergeBoardGameData(bulkGame, result, legBet);
        }
        if (status === true && result) {
          return mergeBoardGameData(bulkGame, result, legBet);
        }
      } catch (err) {
        console.log("resolveOpenBetBoardGames", err);
      }

      return bulkGame || buildFallbackGameFromBet(legBet);
    })
  );
}
